import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import PublicReportHeader from "../reports/PublicReportHeader";
import { reviewScope, useScriptReview } from "../../hooks/useScripts";
import ScriptReviewPanel from "./ScriptReviewPanel";
import { CreatorCell, StatusChip, cardSx, errorMessage, primaryButtonSx } from "./scriptUi";

// /script-review/:token — the no-login page behind a share link. A brand link
// lists every creator on the board; a creator link opens just their script.
// Which one it is comes from the server (the token decides), never the URL shape.

// Reviewers have no account, so edits/remarks are attributed to the name they
// type here. Remembered per browser; storage can be unavailable (private mode),
// so every access is guarded and the page still works without it.
const NAME_KEY = "scriptReviewerName";
const readName = () => {
  try {
    return window.localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
};
const writeName = (name) => {
  try {
    window.localStorage.setItem(NAME_KEY, name);
  } catch {
    // not persisted — fine, it's asked for again next visit
  }
};

const NameDialog = ({ open, initial, onSave }) => {
  const [name, setName] = useState(initial);
  return (
    <Dialog open={open} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>What's your name?</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Typography sx={{ fontSize: 13.5, color: "#6B7280", mb: 1.5 }}>
          It's shown next to your edits, approvals and remarks so the team knows who reviewed.
        </Typography>
        <TextField
          fullWidth
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: 80 }}
          onKeyDown={(e) => e.key === "Enter" && name.trim() && onSave(name.trim())}
          placeholder="Your name"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="contained" disabled={!name.trim()} onClick={() => onSave(name.trim())} sx={primaryButtonSx}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ScriptReviewPage = () => {
  const { token } = useParams();
  const { data, isLoading, error } = useScriptReview(token);
  const [reviewerName, setReviewerName] = useState(readName);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const scripts = useMemo(() => data?.scripts ?? [], [data]);
  const isBrand = data?.party === "brand";

  useEffect(() => {
    if (!selectedId && scripts.length) setSelectedId(scripts[0].id);
  }, [scripts, selectedId]);

  // Only the brand is asked — a creator link already knows who it's for and
  // falls back to the creator's handle server-side.
  useEffect(() => {
    if (isBrand && !reviewerName) setNameDialogOpen(true);
  }, [isBrand, reviewerName]);

  const scope = useMemo(() => reviewScope(token, reviewerName), [token, reviewerName]);

  const saveName = (name) => {
    writeName(name);
    setReviewerName(name);
    setNameDialogOpen(false);
  };

  const shell = (children) => (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)" }}>
      <PublicReportHeader
        campaignName={data?.plan?.name}
        badgeLabel={data ? (isBrand ? "Brand review" : "Creator review") : "Script review"}
      />
      <Box sx={{ pt: 10, pb: 4, px: { xs: 2, sm: 3 }, maxWidth: 1200, mx: "auto" }}>{children}</Box>
    </Box>
  );

  if (isLoading) {
    return shell(
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#FF5200" }} />
      </Box>
    );
  }
  if (error) {
    return shell(
      <Box sx={{ ...cardSx, p: 4, textAlign: "center", maxWidth: 520, mx: "auto" }}>
        <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1 }}>This link isn't available</Typography>
        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>{errorMessage(error, "Please ask the team for a new link.")}</Typography>
      </Box>
    );
  }

  const header = (
    <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 2, flexWrap: "wrap" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {isBrand ? "Review creator scripts" : "Review your script"}
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: "#6B7280" }}>
          {data.plan.brandName ? `${data.plan.brandName} · ` : ""}
          {isBrand ? "Edit the script, leave remarks, or approve it." : "Edit the script or leave remarks — the brand gives the final approval."}
        </Typography>
      </Box>
      {isBrand && reviewerName && (
        <Button size="small" onClick={() => setNameDialogOpen(true)} sx={{ textTransform: "none", color: "#6B7280" }}>
          Reviewing as {reviewerName} · change
        </Button>
      )}
    </Box>
  );

  if (!scripts.length) {
    return shell(
      <>
        {header}
        <Box sx={{ ...cardSx, p: 4, textAlign: "center" }}>
          <Typography sx={{ color: "#6B7280" }}>There are no scripts to review yet.</Typography>
        </Box>
      </>
    );
  }

  return shell(
    <>
      {header}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: isBrand ? "300px 1fr" : "1fr" }, gap: 2.5, alignItems: "start" }}>
        {isBrand && (
          <Box sx={{ ...cardSx, p: 1, maxHeight: { md: "calc(100vh - 200px)" }, overflowY: "auto" }}>
            {scripts.map((s) => (
              <Box
                key={s.id}
                onClick={() => setSelectedId(s.id)}
                sx={{
                  p: 1.25,
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                  bgcolor: s.id === selectedId ? "rgba(255,82,0,0.08)" : "transparent",
                  "&:hover": { bgcolor: s.id === selectedId ? "rgba(255,82,0,0.08)" : "#F9FAFB" },
                }}
              >
                <CreatorCell script={s} />
                <Box sx={{ pl: "46px" }}><StatusChip status={s.status} /></Box>
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ ...cardSx, p: { xs: 2, sm: 3 } }}>
          {selectedId && <ScriptReviewPanel scope={scope} scriptId={selectedId} viewer={data.party} />}
        </Box>
      </Box>
      <NameDialog key={String(nameDialogOpen)} open={nameDialogOpen} initial={reviewerName} onSave={saveName} />
    </>
  );
};

export default ScriptReviewPage;
